<template>
  <LineChart
    :options="chartOptions"
    :data="calculatedData"
    :plugins="plugins"
  />
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import { Line as LineChart } from "vue-chartjs"
import type {
  ChartData,
  Plugin,
  ChartOptions,
} from "chart.js"
import "chartjs-adapter-date-fns"
import type { MerchantCurrencyCode } from "@/utils/currency"

export type ChartLineDateDataset = {
  label: string
  data: number[]
  color?: string | undefined
  overview: "total" | "maximum"
}

export type HoveredDataPoint = {
  index: number | undefined
  datasets: Array<{
    label: string
    value: number
    color?: string | undefined
  }>
  label: {
    start: Date
    end: Date
  }
}

type Granularity = "hour" | "day" | "week" | "month"

ChartJS.register(...registerables)

type Label = {
  start: Date
  end: Date
}
const props = withDefaults(
  defineProps<{
    id: string
    datasets: Array<ChartLineDateDataset>
    labels: Label[]
    granularity?: Granularity | undefined
    isCumulative?: boolean
    period: Granularity | "custom"
    yAxesFormatter: "currency" | "number"
    currency: MerchantCurrencyCode
  }>(),
  {
    isCumulative: false,
    granularity: undefined,
    yAxesDefinitions: undefined,
  }
)

const emit = defineEmits<{
  (
    event: "hover-datapoint",
    datapoint: HoveredDataPoint
  ): void
}>()

const minStart = computed(() => {
  const starts = props.labels.map(({ start }) => start)
  return starts.length ? DateFns.min(starts) : new Date()
})

const maxEnd = computed(() => {
  const ends = props.labels.map(({ end }) => end)
  return ends.length ? DateFns.max(ends) : new Date()
})

const buildCumulativeData = (data: number[]) => {
  let mapReduced: number[] = [0]
  let cumulativeSum = 0
  for (const value of data) {
    cumulativeSum = cumulativeSum + value
    mapReduced = [...mapReduced, cumulativeSum]
  }
  return mapReduced
}

const calculatedLabels = computed(() => {
  return props.isCumulative && props.labels.length >= 0
    ? [
        ...props.labels.map(({ start }) => start),
        maxEnd.value,
      ]
    : props.labels.map(({ start }) => start)
})

const colorPrimary = useCssVar("--color-primary")
const calculatedDatasets = computed(() =>
  props.datasets.map((dataset) => ({
    ...dataset,
    data: props.isCumulative
      ? buildCumulativeData(dataset.data)
      : dataset.data,
    pointRadius: 3,
    borderColor: dataset.color,
  }))
)

const calculatedData = computed<
  ChartData<"line", number[], Date>
>(() => ({
  labels: calculatedLabels.value,
  datasets: calculatedDatasets.value,
}))

const colorText = useCssVar("--color-on-background")

const hoveredDatapointForWholeRange =
  computed<HoveredDataPoint>(() => ({
    index: undefined,
    datasets: props.datasets.map((dataset) => ({
      label: dataset.label,
      value:
        dataset.overview === "total"
          ? dataset.data.reduce(
              (accumulator, value) => accumulator + value,
              0
            )
          : dataset.overview === "maximum"
            ? Math.max(...dataset.data)
            : NaN,
      color: dataset.color,
    })),
    label: {
      start: minStart.value,
      end: maxEnd.value,
    },
  }))
const plugins: Plugin<"line">[] = [
  {
    id: "crosshair",
    afterDatasetsDraw: (
      chart_: ChartJS<"line", number[], Date>
    ) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()
      if (activeElements && activeElements.length > 0) {
        const activePoint = activeElements[0]
        const context = chart_.ctx
        const bottomY = chart_.chartArea.bottom
        const x = activePoint.element.x
        const y = activePoint.element.y
        context.save()
        context.beginPath()
        context.moveTo(x, y)
        context.lineTo(x, bottomY)
        context.lineWidth = 2
        context.strokeStyle = `rgb(${colorPrimary.value} / 0.3)`
        context.stroke()
        context.restore()
      }
    },
  },
  {
    id: "hoverEmitIndex",
    afterDatasetDraw: (chart_) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()

      const activePoint =
        activeElements && activeElements.length > 0
          ? activeElements[0]
          : undefined
      const index_ = activePoint
        ? props.isCumulative
          ? activePoint.index >= 1
            ? activePoint.index - 1
            : undefined
          : activePoint.index
        : undefined

      const hoveredDatapoint =
        index_ !== undefined
          ? {
              index: index_,
              datasets: props.datasets.map((dataset) => ({
                label: dataset.label,
                value: dataset.data[index_],
                color: dataset.color,
              })),
              label: {
                start: props.labels[index_].start,
                end: props.labels[index_].end,
              },
            }
          : hoveredDatapointForWholeRange.value
      emit("hover-datapoint", hoveredDatapoint)
    },
    afterEvent: (chart, { event }) => {
      if (event.native?.type === "touchend") {
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
        chart.setActiveElements([])
        chart.update()
      }
    },
  },
]

const chartOptions: ChartOptions<"line"> = reactiveComputed(
  () => ({
    maintainAspectRatio: false,
    animation: false,
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        mode: "nearest",
        intersect: false,
        axis: "x",
      },
      legend: {
        display: false,
      },
    },
    events: [
      "mousemove",
      "mouseout",
      "click",
      "touchstart",
      "touchmove",
      "touchend",
    ],
    // onHover: (...params) => emit("hover-datapoint", params),
    interaction: {
      mode: "index",
      intersect: false,
      axis: "x",
    },
    scales: {
      x: {
        stacked: false,
        type: "time",
        time: {
          displayFormats: {
            month: "MMM",
          },
          unit: props.granularity,
          isoWeekday: true,
        },
        ticks: {
          font: {
            size: 12,
          },
          textStrokeColor: `rgb(${colorText.value} / 0.7)`,
          minRotation: 0,
          maxRotation: 0,
          padding: 8,
          align: "start",
          autoSkipPadding: 8,
          includeBounds: false,
        },
        grid: {
          color: `rgb(${colorText.value} / 0.05)`,
          drawTicks: false,
        },
      },
      y: {
        title: {
          display: false,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          align: "center",
          mirror: true,
          font: { size: 12 },
          callback: (value: unknown) =>
            props.yAxesFormatter === "currency"
              ? value !== 0
                ? formatToCurrencyString(
                    Number(value),
                    props.currency,
                    {
                      compact: true,
                      decimals: false,
                    }
                  )
                : ""
              : formatToNumberWithUnitPrefix(Number(value)),
        },
        stacked: false,
        beginAtZero: true,
      },
    },
  })
)
</script>
